import React, { useState, useEffect } from 'react';
import { Wallet, Deposit, getAvailableBalance, getReservedBalance, getTotalBalance } from '../types';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { DepositModal } from '../components/wallet/DepositModal';
import { SellBillModal } from '../components/buyback/SellBillModal';
import { Wallet as WalletIcon, PlusCircle, ArrowLeftRight, Clock, CheckCircle2, XCircle, Send } from 'lucide-react';
import { PaymentLogo } from '../components/common/BrandLogo';

export const WalletPage: React.FC = () => {
  const { t } = useLanguage();
  const { user, isGuest } = useAuth();
  const [wallet, setWallet] = useState<Wallet | null>(null);
  const [deposits, setDeposits] = useState<Deposit[]>([]);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showSellModal, setShowSellModal] = useState(false);
  const [depositMethod, setDepositMethod] = useState<'kpay' | 'wavepay'>('kpay');
  const [loading, setLoading] = useState(true);

  const loadWalletData = async () => {
    if (isGuest || !user.id) {
      setLoading(false);
      return;
    }
    try {
      const [wData, depList] = await Promise.all([api.getWallet(), api.getDeposits()]);
      if (wData) {
        setWallet(wData.wallet);
        setTransactions(wData.transactions || []);
      }
      setDeposits(depList);
    } catch {
      // Ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWalletData();
  }, [user.id, isGuest]);

  const getDepositStatusBadge = (status: Deposit['status']) => {
    switch (status) {
      case 'credited':
        return (
          <span className="inline-flex items-center text-[10px] font-bold text-emerald-400 bg-emerald-500/15 px-2 py-0.5 rounded-full border border-emerald-500/30">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            {t.status.credited}
          </span>
        );
      case 'under_review':
        return (
          <span className="inline-flex items-center text-[10px] font-bold text-amber-400 bg-amber-500/15 px-2 py-0.5 rounded-full border border-amber-500/30">
            <Clock className="w-3 h-3 mr-1" />
            {t.status.under_review}
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center text-[10px] font-bold text-red-400 bg-red-500/15 px-2 py-0.5 rounded-full border border-red-500/30">
            <XCircle className="w-3 h-3 mr-1" />
            {t.status.rejected}
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center text-[10px] font-bold text-plum-muted bg-surface-page px-2 py-0.5 rounded-full border border-plum-border">
            {t.status.pending}
          </span>
        );
    }
  };



  return (
    <div className="space-y-4 pb-24">
      {/* Wallet Card */}
      <div className="bg-gradient-to-br from-[#0F2942] via-[#0B1D30] to-[#08121E] rounded-3xl p-6 text-white shadow-lg border border-sky-500/25 relative overflow-hidden">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="p-2.5 rounded-2xl bg-white/10 backdrop-blur-md border border-white/10">
              <WalletIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xs font-semibold text-white/90 uppercase tracking-wider">{t.wallet}</h2>
              <span className="text-xs text-white/60 font-mono">
                ID: {user.telegram_id || wallet?.user_id || user.id}
              </span>
            </div>
          </div>
        </div>

        <div className="mt-6">
          <span className="text-3xl font-bold tracking-tight">
            {getAvailableBalance(wallet).toLocaleString()}
          </span>
          <span className="text-sm ml-1.5 text-white/80 font-medium">Ks</span>
          <p className="text-xs text-white/70 mt-1">{t.available}</p>
        </div>

        {/* Balance details */}
        <div className="grid grid-cols-2 gap-2 mt-5 pt-4 border-t border-white/10 text-xs">
          <div>
            <span className="text-white/60">{t.balance}:</span>
            <p className="font-semibold">{getTotalBalance(wallet).toLocaleString()} Ks</p>
          </div>
          <div>
            <span className="text-white/60">{t.reserved}:</span>
            <p className="font-semibold text-amber-300">
              {getReservedBalance(wallet).toLocaleString()} Ks
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-2 gap-2.5 mt-5 pt-4 border-t border-white/10">
          <button
            onClick={() => {
              setDepositMethod('kpay');
              setShowDepositModal(true);
            }}
            className="flex items-center justify-center space-x-2 bg-primary hover:bg-primary-hover active:scale-[0.98] font-bold py-2.5 px-3 rounded-xl text-xs transition shadow-sm touch-target text-white"
          >
            <PlusCircle className="w-4 h-4 text-white" />
            <span className="text-white font-bold">{t.deposit}</span>
          </button>

          <button
            onClick={() => setShowSellModal(true)}
            className="flex items-center justify-center space-x-2 bg-white/10 hover:bg-white/15 active:scale-[0.98] text-white border border-white/20 font-semibold py-2.5 px-3 rounded-xl text-xs transition backdrop-blur-sm touch-target"
          >
            <ArrowLeftRight className="w-4 h-4 text-white" />
            <span>{t.sellBill}</span>
          </button>
        </div>
      </div>

      {/* Guest Notice Banner */}
      {isGuest && (
        <div className="bg-surface-card rounded-2xl border border-plum-border p-3.5 flex items-center justify-between">
          <div className="flex items-center space-x-2.5">
            <div className="w-9 h-9 rounded-xl bg-primary/15 text-primary flex items-center justify-center shrink-0">
              <Send className="w-4 h-4" />
            </div>
            <div>
              <h4 className="text-xs font-bold text-plum-dark">Telegram Bot ဖြင့် ချိတ်ဆက်ရန်</h4>
              <p className="text-[10px] text-plum-muted">ငွေလက်ကျန်များကို Bot တွင် သိမ်းထားနိုင်ပါသည်</p>
            </div>
          </div>
          <a
            href="https://t.me/pluvp_bot"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-primary hover:bg-primary-dark text-white px-3 py-1.5 rounded-xl text-xs font-semibold shrink-0 transition"
          >
            ချိတ်ဆက်မည်
          </a>
        </div>
      )}

      {/* Deposit Requests */}
      <div>
        <div className="flex items-center justify-between px-1 mb-2.5">
          <h3 className="text-xs font-bold text-plum-muted uppercase tracking-wider">
            {t.depositHistory} ({deposits.length})
          </h3>
        </div>

        <div className="space-y-2.5">
          {deposits.map((dep) => (
            <div
              key={dep.id}
              className="bg-surface-card rounded-2xl border border-plum-border p-4 shadow-xs flex items-center justify-between"
            >
              <div className="flex items-center space-x-3">
                <PaymentLogo
                  method={dep?.payment_method || 'kpay'}
                  size="md"
                  className="rounded-xl shadow-xs border border-plum-border/40"
                />
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="font-bold text-plum-dark text-sm">
                      +{(dep?.amount || 0).toLocaleString()} Ks
                    </span>
                    <span className="text-[10px] text-plum-muted uppercase font-mono">
                      {dep?.payment_method || 'Deposit'}
                    </span>
                  </div>
                  <p className="text-[10px] text-plum-muted mt-0.5 font-mono">
                    Ref: {dep?.tx_ref || dep?.id || 'N/A'}
                  </p>
                </div>
              </div>

              <div>{getDepositStatusBadge(dep.status)}</div>
            </div>
          ))}

          {deposits.length === 0 && !loading && (
            <div className="p-8 text-center bg-surface-card rounded-2xl border border-plum-border text-plum-muted text-xs">
              ငွေသွင်းမှတ်တမ်း မရှိသေးပါ။
            </div>
          )}
        </div>
      </div>

      {/* Deposit Modal */}
      {showDepositModal && (
        <DepositModal
          initialMethod={depositMethod}
          onClose={() => setShowDepositModal(false)}
          onSuccess={() => {
            loadWalletData();
          }}
        />
      )}

      {/* Sell Bill Modal */}
      {showSellModal && (
        <SellBillModal
          onClose={() => setShowSellModal(false)}
          onSuccess={() => {
            loadWalletData();
          }}
        />
      )}
    </div>
  );
};
